'use client'

import Image from 'next/image'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { motion } from 'framer-motion'
import { useState, useSyncExternalStore } from 'react'
import { useTranslations } from 'next-intl'
import { cn } from '@/lib/utils'
import { Container } from '@/components/layout/container'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { signOutAction } from '@/app/sign-in/actions'
import { LanguageSwitcher } from '@/components/layout/language-switcher'
import { MenuButton } from '@/components/layout/menu-button'
import { useContactDialog } from '@/components/contact/contact-dialog'
import { glass } from '@/lib/styles'
import type { AppSession } from '@/lib/auth-utils'

/** Due varianti animate a confronto: 'pill' (pillola flottante, blur che
    appare allo scroll) o 'island' (barra larga che morfa in capsula). */
const HEADER_DESIGN: 'pill' | 'island' = 'pill'

const NAV_ITEMS = [
  { href: '/#servizi', labelKey: 'header.services' },
  { href: '/#casi', labelKey: 'header.examples' },
  { href: '/#pricing', labelKey: 'header.pricing' },
] as const

function getInitials(value: string): string {
  return (
    value
      .split(/[\s.@]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || 'U'
  )
}

function subscribeToScroll(callback: () => void): () => void {
  window.addEventListener('scroll', callback, { passive: true })
  return () => window.removeEventListener('scroll', callback)
}

function subscribeToDesktop(callback: () => void): () => void {
  const mql = window.matchMedia('(min-width: 768px)')
  mql.addEventListener('change', callback)
  return () => mql.removeEventListener('change', callback)
}

export type HeaderVariant = 'default' | 'contained' | 'dashboard'

interface HeaderProps {
  variant?: HeaderVariant
  session: AppSession
}

export function Header({ variant = 'contained', session }: HeaderProps) {
  const pathname = usePathname()
  const router = useRouter()
  const t = useTranslations()
  const { openContact } = useContactDialog()
  const [menuOpen, setMenuOpen] = useState(false)
  // Stato esterno (scroll, host) letto con useSyncExternalStore: niente
  // setState sincrono in effect e snapshot SSR coerente.
  const scrolled = useSyncExternalStore(
    subscribeToScroll,
    () => window.scrollY > 16,
    () => false,
  )
  // Su mobile l'header resta nello stato a riposo (niente sfondo, non richiuso):
  // la variante "scrolled" scatta solo da md in su.
  const isDesktop = useSyncExternalStore(
    subscribeToDesktop,
    () => window.matchMedia('(min-width: 768px)').matches,
    () => false,
  )
  const collapsed = scrolled && isDesktop
  const isAnimated = variant === 'default' || variant === 'contained'

  const handleLogoClick = (e: React.MouseEvent) => {
    if (!isAnimated) return
    if (pathname !== '/') return
    e.preventDefault()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSignOut = async () => {
    // Il redirect lo fa la server action; il refresh allinea l'header.
    await signOutAction()
    router.refresh()
  }

  const isAuthed = !!session
  const userName = 'Admin'
  const userEmail = session?.email ?? ''
  const initials = getInitials(userEmail || userName)

  const logoSize = 120

  const Logo = (
    <Link
      href={variant === 'dashboard' ? '/dashboard' : '/'}
      onClick={handleLogoClick}
      className="flex items-center gap-2"
    >
      <Image
        src="/logo.svg"
        alt="Noxis"
        width={logoSize}
        height={logoSize}
        priority
        className={cn(
          variant === 'dashboard' ? '!h-4 !w-auto object-contain invert' : 'h-4 w-auto object-contain invert transition-[filter] duration-300',
        )}
      />
    </Link>
  )

  const AuthAction = isAuthed ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Account menu"
          className="rounded-full focus:outline-none focus:ring-2 focus:ring-primary/40 transition-transform hover:scale-105 active:scale-95"
        >
          <Avatar className="h-9 w-9">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="text-sm font-medium leading-none">{userName}</span>
          <span className="text-xs text-muted-foreground leading-none">{userEmail}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/dashboard">Dashboard</Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut} variant="destructive">
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null

  if (variant === 'dashboard') {
    return (
      <header className="fixed top-0 left-0 right-0 z-50 w-full bg-neutral-900 text-neutral-100">
        <Container>
          <div className="flex items-center justify-between h-16">
            {Logo}
            <div className="flex items-center gap-4">{AuthAction}</div>
          </div>
        </Container>
      </header>
    )
  }

  const inner = (
    <>
      {Logo}

      {/* Cluster destro con ritmo uniforme (gap-7 = 28px): link e utility
          condividono la stessa spaziatura. Su mobile resta compatto (gap-2). */}
      <div className="flex items-center gap-2 md:gap-7">
        {/* Voci a destra, testo puro */}
        <ul className="hidden md:flex items-center gap-7">
          {NAV_ITEMS.map((item) => {
            const active =
              !item.href.includes('#') &&
              (pathname === item.href || pathname.startsWith(`${item.href}/`))
            return (
              <li key={item.href}>
                <Link
                  href={item.href}
                  className={cn(
                    'text-sm font-medium tracking-tight transition-colors',
                    active ? 'text-foreground' : 'text-foreground/60 hover:text-foreground',
                  )}
                >
                  {t(item.labelKey)}
                </Link>
              </li>
            )
          })}
        </ul>

        <LanguageSwitcher className="hidden md:inline-flex" />

        {/* CTA sempre visibile, anche su mobile: il sito ha un solo obiettivo, la call */}
        <button
          type="button"
          onClick={() => openContact('automation')}
          className={cn(
            'inline-flex items-center justify-center rounded-full px-4 py-2 md:px-5 md:py-2.5 cursor-pointer select-none',
            'bg-primary text-primary-foreground text-sm font-semibold tracking-tight',
            'shadow-[0_1px_2px_oklch(0.705_0.213_47.6/0.3),0_10px_24px_-10px_oklch(0.705_0.213_47.6/0.55)]',
            'transition-all duration-300 hover:bg-foreground hover:text-background hover:-translate-y-px active:translate-y-0',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          )}
        >
          {t('header.lets_talk')}
        </button>

        <span className="md:hidden">
          <MenuButton open={menuOpen} onOpenChange={setMenuOpen} />
        </span>
        {AuthAction}
      </div>
    </>
  )

  // ── Variante B: "island" — barra larga che allo scroll morfa in capsula ──
  if (HEADER_DESIGN === 'island') {
    return (
      <header className="fixed top-0 left-0 right-0 z-[100] flex justify-center px-4 pointer-events-none">
        <motion.nav
          initial={{ y: -20, opacity: 0, maxWidth: 1240 }}
          animate={{
            y: 0,
            opacity: 1,
            maxWidth: scrolled ? 760 : 1240,
            marginTop: scrolled ? 10 : 16,
            height: scrolled ? 52 : 60,
            paddingLeft: scrolled ? 20 : 0,
            paddingRight: scrolled ? 20 : 0,
          }}
          transition={{ type: 'spring', stiffness: 220, damping: 28 }}
          className="pointer-events-auto relative flex w-full items-center justify-between gap-4 rounded-full"
        >
          <motion.span
            aria-hidden
            initial={false}
            animate={{ opacity: scrolled ? 1 : 0 }}
            transition={{ duration: 0.3 }}
            className={cn(glass, 'absolute inset-0 -z-10 rounded-full')}
          />
          {inner}
        </motion.nav>
      </header>
    )
  }

  // ── Variante A: "pill" — a riposo il contenuto è allineato ai bordi del
  // container; allo scroll la pillola appare e si restringe verso il centro ──
  return (
    <header className="fixed top-0 left-0 right-0 z-[100] pt-4 md:pt-5 pointer-events-none">
      <Container>
        <motion.nav
          initial={{ y: -20, opacity: 0 }}
          animate={{
            y: 0,
            opacity: 1,
            // Larghezza sempre = container: allo scroll cambiano solo i padding,
            // così il contenuto rientra dentro la pillola.
            paddingLeft: collapsed ? 24 : 0,
            paddingRight: collapsed ? 24 : 0,
          }}
          transition={{ type: 'spring', stiffness: 220, damping: 28 }}
          className="pointer-events-auto relative flex h-14 w-full items-center justify-between gap-4 rounded-full"
        >
          <motion.span
            aria-hidden
            initial={false}
            animate={{ opacity: collapsed ? 1 : 0 }}
            transition={{ duration: 0.35 }}
            className={cn(glass, 'absolute inset-0 -z-10 rounded-full')}
          />
          {inner}
        </motion.nav>
      </Container>
    </header>
  )
}
